/* eslint-disable no-plusplus */
import base64js from 'base64-js';
import Resizer from 'react-image-file-resizer';

export const base64Decode = (str: any, encoding = 'utf-8') => {
  if (!str) {
    return '';
  }
  const bytes = base64js.toByteArray(str);
  return new TextDecoder(encoding).decode(bytes);
};

export const base64Encode = (str: any, encoding = 'utf-8') => {
  if (!str) {
    return '';
  }
  const bytes = new TextEncoder().encode(str);
  return base64js.fromByteArray(bytes);
};

export function arrayBufferToBase64(buffer: any) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export function base64ToArrayBuffer(base64: any) {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

export function fileToString(file: File) {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onloadend = () => {
        resolve(arrayBufferToBase64(reader.result));
      };
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * ЖЁСТКИЙ предел длины base64 в теле запроса, в символах (= байтах тела).
 *
 * Ограничитель здесь НЕ nginx: на обоих шлюзах (test.eosi.kz и eosi.kz) `client_max_body_size`
 * поднят до 10m, то есть тело до 10 МиБ доезжает. Предел задан заявленным лимитом на документ
 * `doc_types.max_size` = 2 МиБ (одинаков у всех 13 типов). Файл 2 МиБ в base64 занимает
 * 2 МиБ * 4/3 — это и есть значение ниже. Серверной проверки max_size сейчас нет, так что
 * фронт соблюдает её сам; всё, что больше, отклоняем с внятным текстом.
 *
 * Это НЕ цель сжатия, а именно граница отказа: сюда упирается PDF, который сжать нечем.
 */
export const SCAN_BASE64_MAX_BYTES = Math.floor((2 * 1024 * 1024 * 4) / 3);

/**
 * ЦЕЛЬ адаптивного сжатия изображений, в символах base64.
 *
 * Меньше жёсткого предела намеренно: 1 МиБ base64 (~768 КиБ бинарника) — разумный компромисс
 * между читаемостью скана и объёмом хранилища, картинке такого бюджета хватает с запасом
 * (замер: скан A4 300 dpi при 2200 px / q0.8 укладывается в ~145 КБ). Недостижение цели —
 * НЕ повод отказать: если результат всё же укладывается в SCAN_BASE64_MAX_BYTES, его надо
 * отправлять.
 */
export const SCAN_BASE64_TARGET_BYTES = 1024 * 1024;

/**
 * ЖЁСТКАЯ граница отказа ДЛЯ PDF, в символах base64.
 *
 * ТЕПЕРЬ РАВНА ГРАНИЦЕ ДЛЯ КАРТИНОК — заявленным `doc_types.max_size` = 2 МиБ. Раньше у PDF
 * предел был свой и заметно выше (тело шлюза 10 МиБ минус запас ≈ 7.45 МиБ файла): считалось,
 * что документ, который не удалось ужать, лучше пропустить, чем отклонить. Решение изменено:
 * лимит на документ единый для всех типов файлов, а то, что не влезло, пользователь сжимает
 * сам перед загрузкой.
 *
 * ЧТО ЭТО ЗНАЧИТ НА ПРАКТИКЕ: PDF, который после растеризации остаётся больше 2 МиБ,
 * отклоняется с внятным текстом. Замер на реальном 47-страничном скане («Паспорт заключение»,
 * 7.1 МБ): нижняя ступень лестницы даёт 3.9 МБ — такой документ теперь не пройдёт, его надо
 * сжимать вручную или делить на файлы. Это осознанный размен в пользу предсказуемого объёма
 * хранилища.
 *
 * Константа оставлена отдельной (а не заменена на SCAN_BASE64_MAX_BYTES по всему коду), чтобы
 * пороги PDF и картинок можно было снова развести одной строкой, не переписывая логику.
 */
export const PDF_BASE64_HARD_LIMIT_BYTES = SCAN_BASE64_MAX_BYTES;

/**
 * ЦЕЛЬ сжатия PDF, в символах base64 — то есть порог, НАЧИНАЯ С КОТОРОГО PDF вообще сжимается.
 *
 * ПОЧЕМУ ОТДЕЛЬНАЯ КОНСТАНТА, а не общая с картинками: раньше целью для PDF служила граница
 * отказа `SCAN_BASE64_MAX_BYTES` (2 МиБ = заявленный `doc_types.max_size`), и всё, что меньше
 * двух мегабайт, уходило на сервер как есть. Уменьшить саму `SCAN_BASE64_MAX_BYTES` нельзя —
 * она задаёт границу отказа для изображений, это другая роль.
 *
 * ПОЧЕМУ 1 МиБ. Замер на типовых сканах ровно этого диапазона (стенд, верхняя ступень лестницы
 * 2200 px / q0.80 — для одно-двухстраничных документов бюджет на страницу большой, и агрессивные
 * ступени не нужны):
 *   скан 200 dpi, 1 стр., 1.86 МБ -> 524 КБ (27%), 0.85 с
 *   скан 300 dpi, 1 стр., 2.09 МБ -> 388 КБ (18%), 1.00 с
 *   скан 200 dpi, 2 стр., 3.05 МБ -> 995 КБ (31%), 1.14 с
 * То есть файлы 1–2 МБ ужимаются в 3.5–5 раз ценой примерно секунды, причём на МАКСИМАЛЬНОМ
 * качестве лестницы — читаемость мелкого шрифта не страдает (2200 px по длинной стороне ≈ 190 dpi
 * для A4). С учётом того, что 88% загружаемых сканов — PDF, экономия хранилища и трафика заметная.
 *
 * Ниже 1 МиБ порог не опускаем сознательно: сжатие начало бы срабатывать почти на каждом PDF,
 * и секунда ожидания стала бы постоянной ценой любой загрузки.
 *
 * Недостижение цели — НЕ повод отказать: решение принимает вызывающий код по своей границе
 * (PDF_BASE64_HARD_LIMIT_BYTES).
 */
export const PDF_BASE64_TARGET_BYTES = Math.floor((1024 * 1024 * 4) / 3);

/**
 * Лестница параметров сжатия: максимальная сторона + качество JPEG.
 * Перебирается по порядку, пока длина итоговой base64 не уложится в бюджет.
 *
 * ПОЧЕМУ качество снижается вместе со стороной: react-image-file-resizer умеет только
 * УМЕНЬШАТЬ размеры — фото меньше 1980px по обеим сторонам не ужимается вовсе, и лестница
 * из одних только сторон для него не дала бы никакого эффекта.
 */
const COMPRESSION_STEPS: ReadonlyArray<{ maxSide: number; quality: number }> = [
  { maxSide: 1980, quality: 90 },
  { maxSide: 1980, quality: 80 },
  { maxSide: 1600, quality: 75 },
  { maxSide: 1280, quality: 70 },
  { maxSide: 1024, quality: 60 },
  { maxSide: 800, quality: 50 }
];

/** Результат адаптивного сжатия изображения. */
export interface CompressedImage {
  /** base64 БЕЗ префикса `data:...;base64,` — ровно то, что уходит в теле запроса */
  base64: string;
  /** длина base64 в символах (= размер полезной нагрузки в байтах) */
  size: number;
  /** уложились ли в бюджет; false — вернули самый компактный из полученных вариантов */
  withinBudget: boolean;
  /** параметры шага, давшего результат (для логов/диагностики) */
  maxSide: number;
  quality: number;
}

/**
 * Один проход resizer'а: возвращает data-URL (`data:image/jpeg;base64,...`).
 * Библиотека при невалидном файле бросает синхронно и НЕ вызывает колбэк — без try/catch
 * промис навсегда остался бы pending и спиннер висел бы вечно.
 */
const resizeImageToDataUrl = (file: File, maxSide: number, quality: number): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    try {
      Resizer.imageFileResizer(
        file,
        maxSide,
        maxSide,
        'JPEG',
        quality,
        0,
        (uri) => {
          if (typeof uri === 'string') {
            resolve(uri);
            return;
          }
          reject(new Error('Resizer вернул не base64-строку'));
        },
        'base64'
      );
    } catch (e) {
      reject(e);
    }
  });

/**
 * Адаптивное сжатие изображения под бюджет в байтах.
 *
 * Идём по лестнице COMPRESSION_STEPS и возвращаем ПЕРВЫЙ результат, чья base64-строка
 * укладывается в бюджет. Меряем именно длину base64, а не размер бинарника: в теле запроса
 * уходит строка, и лимит nginx считается по ней.
 *
 * Если ни один шаг не подошёл — возвращаем самый компактный вариант с withinBudget=false.
 * Это НЕ приговор: решение «отправлять или отказать» принимает вызывающий код, сверяясь
 * с жёстким пределом SCAN_BASE64_MAX_BYTES.
 */
export const compressImageToBudget = async (
  file: File,
  budgetBytes: number = SCAN_BASE64_TARGET_BYTES
): Promise<CompressedImage> => {
  let smallest: CompressedImage | null = null;

  for (let i = 0; i < COMPRESSION_STEPS.length; i++) {
    const { maxSide, quality } = COMPRESSION_STEPS[i];
    // Шаги принципиально последовательны: следующий запускаем только если предыдущий не влез.
    // eslint-disable-next-line no-await-in-loop
    const dataUrl = await resizeImageToDataUrl(file, maxSide, quality);
    const base64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
    const candidate: CompressedImage = {
      base64,
      size: base64.length,
      withinBudget: base64.length <= budgetBytes,
      maxSide,
      quality
    };

    if (candidate.withinBudget) return candidate;
    if (!smallest || candidate.size < smallest.size) smallest = candidate;
  }

  return smallest ?? { base64: '', size: 0, withinBudget: false, maxSide: 0, quality: 0 };
};

/**
 * Совместимая обёртка над одним проходом сжатия (1980px, q=90) — возвращает data-URL,
 * как и раньше. Оставлена для легаси-вызовов (widgets/registration/**), новые сценарии
 * должны использовать compressImageToBudget.
 */
export const resizeFile = (file: File): Promise<string> => resizeImageToDataUrl(file, 1980, 90);

interface ConvertedFileData {
  bytes: number[];
  base64StringFile: string;
  fileName: string;
  fileType: string;
}

export const getBase64 = (file: File) => {
  const reader = new FileReader();
  return new Promise<ConvertedFileData>((resolve, reject) => {
    reader.onerror = () => {
      reader.abort();
      reject(new Error('Error parsing file'));
    };
    reader.onload = function () {
      if (!this.result) return;

      const bytes = Array.from(new Uint8Array(this.result as ArrayBuffer));

      const base64StringFile = btoa(bytes.map((item) => String.fromCharCode(item)).join(''));

      resolve({
        bytes,
        base64StringFile,
        fileName: file.name,
        fileType: file.type
      });
    };
    reader.readAsArrayBuffer(file);
  });
};
